import { useState } from "react";
import { X, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Dialog per il cambio password dell'utente autenticato.
 * - Richiede password attuale + nuova + conferma
 * - Minimo 12 caratteri, con 1 maiuscola / 1 minuscola / 1 cifra (validato anche lato backend)
 * - Dopo l'avvenuto cambio il backend invalida le altre sessioni
 */
export default function ChangePasswordDialog({ open, onClose }) {
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const reset = () => {
    setOldPwd("");
    setNewPwd("");
    setConfirm("");
    setBusy(false);
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 12) {
      toast.error("La nuova password deve avere almeno 12 caratteri");
      return;
    }
    if (!/[A-Z]/.test(newPwd) || !/[a-z]/.test(newPwd) || !/[0-9]/.test(newPwd)) {
      toast.error("Serve almeno 1 maiuscola, 1 minuscola e 1 cifra");
      return;
    }
    if (newPwd !== confirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    if (newPwd === oldPwd) {
      toast.error("La nuova password deve essere diversa");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldPwd,
        new_password: newPwd,
        forced: false,
      });
      toast.success("Password aggiornata. Le altre sessioni sono state invalidate.");
      reset();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Impossibile cambiare la password";
      toast.error(typeof msg === "string" ? msg : "Errore durante il cambio password");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="change-password-dialog"
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={close}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-[#0a0a0a] border border-white/10 shadow-[0_0_40px_rgba(255,0,51,0.15)] p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-[#ff0033]" />
            <h2 className="text-base font-semibold">Cambia password</h2>
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="change-password-close"
            className="p-1.5 rounded-lg hover:bg-white/5 text-white/60"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/50">Password attuale</label>
            <input
              type="password"
              autoComplete="current-password"
              value={oldPwd}
              onChange={(e) => setOldPwd(e.target.value)}
              required
              data-testid="change-password-old"
              className="w-full mt-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#ff0033]"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/50">Nuova password (min 12)</label>
            <input
              type="password"
              autoComplete="new-password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              minLength={12}
              required
              data-testid="change-password-new"
              className="w-full mt-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#ff0033]"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/50">Conferma nuova password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={12}
              required
              data-testid="change-password-confirm"
              className="w-full mt-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#ff0033]"
            />
          </div>

          <p className="text-[11px] text-white/40 leading-relaxed">
            Al termine le altre sessioni attive su altri dispositivi verranno chiuse.
          </p>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={busy}
            data-testid="change-password-cancel"
            className="px-4 py-2 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="change-password-submit"
            className="btn-neon px-5 py-2 rounded-lg text-xs inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {busy ? "Aggiornamento…" : "Aggiorna"}
          </button>
        </div>
      </form>
    </div>
  );
}
